import React from "react";
import { Card } from "../../../components/shared/Card/Card";
import { useRequiredTenant } from "../../../contexts/TenantContext";
import { formatCurrency } from "../../../utils/formatters";

interface TaxInformationProps {
  propertyData: {
    taxInfo?: {
      assessedValue?: number;
      assessedLandValue?: number;
      assessedImprovementValue?: number;
      taxAmount?: string;
      assessmentYear?: number;
    };
  };
  className?: string;
}

export const TaxInformation: React.FC<TaxInformationProps> = ({
  propertyData,
  className = "",
}) => {
  const { colors } = useRequiredTenant().branding;

  if (!propertyData.taxInfo) return null;

  const taxInfo = propertyData.taxInfo;
  const annualTaxAmount = parseFloat(taxInfo.taxAmount || "0");
  const monthlyTaxAmount = annualTaxAmount / 12;

  return (
    <Card title="Tax Information" className={className} elevation="md">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Assessment Details */}
        <div>
          <div className="bg-gray-200 p-3 rounded-t-lg border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">
              Assessment Details
            </h3>
          </div>
          <div className="divide-y divide-gray-100 [&>*:nth-child(even)]:bg-gray-100 rounded-b-lg border-x border-b border-gray-200">
            {taxInfo.assessedValue && (
              <div className="flex justify-between py-3 px-4">
                <span className="text-sm text-gray-600">
                  Total Assessed Value
                </span>
                <span
                  className="text-sm font-medium"
                  style={{ color: colors.primary }}
                >
                  {formatCurrency(taxInfo.assessedValue)}
                </span>
              </div>
            )}
            {taxInfo.assessedLandValue && (
              <div className="flex justify-between py-3 px-4">
                <span className="text-sm text-gray-600">Land Value</span>
                <span
                  className="text-sm font-medium"
                  style={{ color: colors.primary }}
                >
                  {formatCurrency(taxInfo.assessedLandValue)}
                </span>
              </div>
            )}
            {taxInfo.assessedImprovementValue && (
              <div className="flex justify-between py-3 px-4">
                <span className="text-sm text-gray-600">Improvement Value</span>
                <span
                  className="text-sm font-medium"
                  style={{ color: colors.primary }}
                >
                  {formatCurrency(taxInfo.assessedImprovementValue)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tax Details */}
        <div>
          <div className="bg-gray-200 p-3 rounded-t-lg border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700">Tax Details</h3>
          </div>
          <div className="divide-y divide-gray-100 [&>*:nth-child(even)]:bg-gray-100 rounded-b-lg border-x border-b border-gray-200">
            <div className="flex justify-between py-3 px-4">
              <span className="text-sm text-gray-600">Annual Tax Amount</span>
              <span
                className="text-sm font-medium"
                style={{ color: colors.primary }}
              >
                {formatCurrency(annualTaxAmount)}
              </span>
            </div>
            <div className="flex justify-between py-3 px-4">
              <span className="text-sm text-gray-600">
                Monthly Tax Estimate
              </span>
              <span
                className="text-sm font-medium"
                style={{ color: colors.primary }}
              >
                {formatCurrency(monthlyTaxAmount)}
              </span>
            </div>
            {taxInfo.assessmentYear && (
              <div className="flex justify-between py-3 px-4">
                <span className="text-sm text-gray-600">Assessment Year</span>
                <span
                  className="text-sm font-medium"
                  style={{ color: colors.primary }}
                >
                  {taxInfo.assessmentYear}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tax Distribution Chart - Placeholder for future enhancement */}
      <div
        className={`mt-6 p-4 rounded-lg`}
        style={{ backgroundColor: `${colors.background}20` }}
      >
        <div className="text-sm text-center" style={{ color: colors.text }}>
          Tax rates and assessments are subject to change. Contact your local
          tax assessor for the most current information.
        </div>
      </div>
    </Card>
  );
};

export default TaxInformation;
